/*
 * Sanitizes values for safe client logging.
 */
import { MONITORING_SCRUB_FIELDS } from '../../common/monitoring/monitoringScrubFieldsConstants';

export type SanitizeOptions = {
    maxStringLength?: number; // default 200
    redactKeys?: string[]; // additional keys to redact
    depth?: number; // internal use
};

// Default maximum number of characters from any single string we keep before truncating.
// This keeps log payloads small and avoids leaking large blobs of user content.
const MAX_STRING_LENGTH = 200;
const REDACT_TOKEN = '[REDACTED]';
// Maximum structural depth to fully traverse. At or beyond this depth we summarize nested objects/arrays.
// Depth meaning: root=0, first level children=1, etc.
// The depth is somewhat arbitrary but aims to balance useful structure vs log verbosity.
const MAX_TRAVERSAL_DEPTH = 4;
const DEFAULT_REDACT_KEYS = [
    'email',
    'name',
    'fullName',
    'title',
    'content',
    'body',
    'description',
    'text',
    'fileName',
    // Some payloads (e.g. Axios config.data) serialize objects to JSON strings using lowercase filename
    // We include both casings to ensure redaction even when nested in stringified JSON blobs.
    'filename',
    'boardName',
    'password',
];
// Shared monitoring scrub fields, matched case-insensitively, so logs sent to any third party redact the same keys.
const MONITORING_SCRUB_FIELD_SET = new Set(MONITORING_SCRUB_FIELDS.map((field) => field.toLowerCase()));
const UNSANITIZED_KEYS = ['fingerprint'];

/**
 * HTTP header names whose values should always be fully redacted. These are lower‑cased for
 * case‑insensitive matching. We intentionally only include headers that routinely carry
 * credentials, tokens, session identifiers or direct PII. Headers that are potentially
 * sensitive but still diagnostically valuable (e.g. Referer, User-Agent, X-Forwarded-For) are
 * intentionally excluded as per current product policy – if policy changes they can be added
 * here or handled via a future "summarize" path.
 */
export const DEFAULT_HTTP_HEADERS_REDACT_KEYS: string[] = [
    'authorization',
    'proxy-authorization',
    'cookie',
    'set-cookie',
    'x-api-key',
    'x-apikey',
    'x-auth-token',
    'x-access-token',
    'x-csrf-token',
    'x-xsrf-token',
    'x-session',
    'x-session-id',
    'session-id',
    'x-user',
    'x-user-id',
    'x-userid',
    'x-user-email',
    'x-email',
    'x-forwarded-user',
    'x-forwarded-email',
    'idempotency-key',
    'client-secret',
    'x-signature',
    'x-webhook-signature',
    'x-hub-signature',
    'x-hub-signature-256',
];

const JSON_PARSE_MAX = 5000; // attempt to parse JSON strings up to this size; larger JSON-like blobs are fully redacted

const TOKEN_PATTERNS: RegExp[] = [
    /^(?:[A-Za-z0-9-_]+\.){2}[A-Za-z0-9-_]+$/, // JWT-ish (three dot-separated base64url segments)
    /[A-Fa-f0-9]{32,}/, // long hex strings
    /[A-Za-z0-9-_]{40,}/, // generic long token-like strings
];

// Basic email-like pattern. We purposefully keep this heuristic lightweight; it will occasionally
// match false positives (e.g. something@localhost) but errs on the side of redaction to avoid
// leaking user PII in log aggregation. We restrict local part / domain part lengths to common
// practical limits to reduce pathological runaway matches inside huge blobs.
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]{1,64}@[A-Z0-9.-]{1,255}\.[A-Z]{2,}\b/gi;

const redactEmailSubstrings = (str: string): string => {
    if (!str || typeof str !== 'string') return str;
    if (!/@/.test(str)) return str; // quick pre-filter
    return str.replace(EMAIL_PATTERN, REDACT_TOKEN);
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    Object.prototype.toString.call(v) === '[object Object]';

/**
 * Preserves logging control fields that external logging services need to receive exactly as supplied.
 */
const shouldPreserveUnsanitizedKey = (key: string): boolean => UNSANITIZED_KEYS.includes(key);

// Attempts to summarize a URL-like string into a compact descriptor, or returns null if not URL-like.
const attemptSummarizeUrl = (str: string): string | null => {
    // Quick heuristic: contains protocol:// or starts with '/' or looks like domain.tld/
    if (!/(https?:\/\/)|^\//i.test(str) && !/^[a-z0-9.-]+\.[a-z]{2,}/i.test(str)) return null;
    try {
        // Use a benign base for relative URLs
        const url = new URL(str, 'https://local');
        const descriptorParts = [
            'url',
            `host=${url.host === 'local' ? '(relative)' : url.host}`,
            `path=${url.pathname || '/'}`,
        ];
        if (url.search) descriptorParts.push('hasQuery');
        if (url.hash) descriptorParts.push('hasHash');
        return `[${descriptorParts.join(' ')}]`;
    } catch {
        return null;
    }
};

// Attempts to parse and sanitize a JSON-looking string.
// Returns sanitized object/array, REDACT_TOKEN (for large JSON), or null if not JSON-like / parse failed.
const attemptSanitizeJsonString = (
    raw: string,
    opts: SanitizeOptions,
    visited: WeakSet<object>,
    recurse: (value: unknown, opts: SanitizeOptions, visited: WeakSet<object>) => unknown,
): unknown => {
    const trimmed = raw.trim();
    const looksJson =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
    if (!looksJson) return null;
    // If it *looks* like JSON but is very large, treat entire content as sensitive and redact.
    if (trimmed.length > JSON_PARSE_MAX) return REDACT_TOKEN;
    // Quick heuristic: ensure it has structural characters beyond just braces.
    if (!/[:,\[\]{}]/.test(trimmed)) return null;
    try {
        const parsed = JSON.parse(trimmed);
        if (isPlainObject(parsed) || Array.isArray(parsed)) {
            return recurse(parsed, { ...opts, depth: (opts.depth || 0) + 1 }, visited);
        }
    } catch {
        return null; // fall back to normal string handling
    }
    return null;
};

// Redacts token-like strings, summarizes URLs, or truncates long content to keep logs small and non-sensitive.
const redactOrSummarizeString = (str: string, max: number): string => {
    if (!str) return str;

    // Detect token-like BEFORE URL summarization so JWTs with dots are still redacted.
    const uniqueChars = new Set(str.split(''));
    for (const pat of TOKEN_PATTERNS) {
        if (pat.test(str)) {
            // Heuristic: don't treat homogenous long strings (e.g. 'aaaaa...') as tokens; allow truncation instead.
            if (uniqueChars.size <= 2 && str.length > max) break; // skip token redaction, fall through to truncation
            return REDACT_TOKEN;
        }
    }

    const urlSummary = attemptSummarizeUrl(str);
    if (urlSummary) return urlSummary;
    // Redact email-like substrings inline (partial redaction) rather than removing the entire value.
    let working = redactEmailSubstrings(str);
    if (working.length > max) working = `${working.slice(0, max - 20)}…[truncated]`;
    return working;
};

type MinimalErrorLike = {
    name?: string;
    message?: string;
    status?: number;
    code?: unknown;
    stack?: string;
};

const sanitizeError = (err: unknown, opts: SanitizeOptions) => {
    if (!(err instanceof Error)) return err;
    const errorLike: MinimalErrorLike = err as MinimalErrorLike;
    const safe: Record<string, unknown> = {
        name: errorLike.name || 'Error',
        // Preserve original error message content but redact tokens / emails / truncate.
        message:
            redactOrSummarizeString(errorLike.message || '', opts.maxStringLength || MAX_STRING_LENGTH) || undefined,
        isSanitized: true,
    };
    if (typeof errorLike.status === 'number') safe.status = errorLike.status;
    if (errorLike.code !== undefined) {
        safe.code = redactOrSummarizeString(String(errorLike.code), opts.maxStringLength || MAX_STRING_LENGTH);
    }
    // Include stack trace but redact email substrings to avoid leaking addresses in error surfaces.
    if (typeof errorLike.stack === 'string') safe.stack = redactEmailSubstrings(errorLike.stack);
    return safe;
};

/**
 * Sanitizes string values. Attempts structured JSON parsing/redaction first, then URL summarization/token redaction
 * and finally length truncation.
 */
const sanitizeStringValue = (
    value: string,
    opts: SanitizeOptions,
    visited: WeakSet<object>,
    recurse: (value: unknown, opts: SanitizeOptions, visited: WeakSet<object>) => unknown,
): unknown => {
    const { maxStringLength = MAX_STRING_LENGTH } = opts;
    const jsonResult = attemptSanitizeJsonString(value, opts, visited, recurse);
    if (jsonResult !== null) return jsonResult; // already sanitized object/array or REDACT_TOKEN for large JSON
    return redactOrSummarizeString(value, maxStringLength);
};

/**
 * Sanitizes array values, respecting depth & circular reference detection.
 */
const sanitizeArrayValue = (
    arr: unknown[],
    opts: SanitizeOptions,
    visited: WeakSet<object>,
    recurse: (value: unknown, opts: SanitizeOptions, visited: WeakSet<object>) => unknown,
): unknown => {
    if (visited.has(arr)) return '[Circular]';
    visited.add(arr);
    if ((opts.depth || 0) >= MAX_TRAVERSAL_DEPTH) return `[array(${arr.length})]`;
    return arr.map((v) => recurse(v, { ...opts, depth: (opts.depth || 0) + 1 }, visited));
};

/**
 * Sanitizes plain object values: redacts sensitive keys, enforces depth constraints, and prevents circular traversal.
 */
const sanitizePlainObjectValue = (
    obj: Record<string, unknown>,
    opts: SanitizeOptions,
    visited: WeakSet<object>,
    recurse: (value: unknown, opts: SanitizeOptions, visited: WeakSet<object>) => unknown,
): unknown => {
    if (visited.has(obj)) return '[Circular]';
    visited.add(obj);
    const currentDepth = opts.depth || 0;
    if (currentDepth >= MAX_TRAVERSAL_DEPTH) return '[object]';
    // Build a case-sensitive set for structural keys and a case-insensitive lookup for HTTP headers.
    const redactKeys = new Set([...(opts.redactKeys || []), ...DEFAULT_REDACT_KEYS]);
    // For HTTP headers we treat keys case-insensitively. We store them lower‑cased.
    const httpHeaderRedactSet = new Set<string>(DEFAULT_HTTP_HEADERS_REDACT_KEYS);
    // Support simple wildcard pattern x-signature-* by checking prefix matches once.
    const httpHeaderWildcardPrefixes = ['x-signature-'];
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (shouldPreserveUnsanitizedKey(k)) {
            out[k] = v;
            continue;
        }

        if (redactKeys.has(k)) {
            out[k] = REDACT_TOKEN;
            continue;
        }
        // HTTP header redaction path: if the key appears to be a header object key OR commonly nested under headers.
        // We apply header redaction heuristics when the object appears to be a headers bag or the key itself is a header name.
        // Heuristic: if the parent object has >1 typical header-ish key (e.g., contains 'authorization' or 'cookie') we assume it's a header bag.
        const lowerKey = k.toLowerCase();
        let shouldRedactHeader = false;
        if (MONITORING_SCRUB_FIELD_SET.has(lowerKey)) {
            out[k] = REDACT_TOKEN;
            continue;
        }
        if (httpHeaderRedactSet.has(lowerKey)) {
            shouldRedactHeader = true;
        } else {
            // Wildcard prefix check
            if (httpHeaderWildcardPrefixes.some((p) => lowerKey.startsWith(p))) shouldRedactHeader = true;
        }
        if (shouldRedactHeader) {
            out[k] = REDACT_TOKEN;
            continue;
        }
        // If next depth would exceed limit, substitute placeholder directly rather than recursing.
        if (currentDepth + 1 >= MAX_TRAVERSAL_DEPTH && (isPlainObject(v) || Array.isArray(v))) {
            out[k] = isPlainObject(v) ? '[object]' : `[array(${(v as unknown[]).length})]`;
            continue;
        }
        out[k] = recurse(v, { ...opts, depth: currentDepth + 1 }, visited);
    }
    return out;
};

// Dispatcher that routes based on runtime type, delegating to the specialised helpers above.
function sanitizeValue(value: unknown, opts: SanitizeOptions, visited: WeakSet<object>): unknown {
    if (value === null || value === undefined) return value as null | undefined;
    const t = typeof value;
    if (t === 'string') return sanitizeStringValue(value as string, opts, visited, sanitizeValue);
    if (t === 'number' || t === 'boolean' || t === 'bigint') return value;
    if (t === 'function') return '[fn]';
    if (t === 'symbol') return (value as symbol).toString();
    if (value instanceof Date) return value.toISOString();
    if (value instanceof RegExp) return value.toString();
    if (value instanceof Error) return sanitizeError(value, opts);
    if (Array.isArray(value)) return sanitizeArrayValue(value, opts, visited, sanitizeValue);
    if (ArrayBuffer.isView(value)) {
        const view = value as ArrayBufferView & { length?: number };
        return `[typed-array length=${typeof view.length === 'number' ? view.length : view.byteLength}]`;
    }
    if (value instanceof ArrayBuffer) return `[array-buffer byteLength=${value.byteLength}]`;
    if (value instanceof Map) return `[map size=${value.size}]`;
    if (value instanceof Set) return `[set size=${value.size}]`;
    if (isPlainObject(value)) return sanitizePlainObjectValue(value, opts, visited, sanitizeValue);
    try {
        if (t === 'object') return Object.prototype.toString.call(value);
        return String(value);
    } catch {
        return '[SANITIZE_ERROR]';
    }
}

export const sanitizeLogValue = (value: unknown, options: SanitizeOptions = {}): unknown => {
    try {
        return sanitizeValue(value, options, new WeakSet());
    } catch {
        return '[SANITIZE_ERROR]';
    }
};
